import { useEffect, useMemo, useRef, useState } from "react";
import { Filter, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// BREEZEAI-1054: label accents mirror the graph node legend and the chip
// component so the whole filter surface (button badge → panel row → chip)
// reads as one visual language.
const LABEL_ACCENT: Record<
  string,
  { dot: string; bg: string; text: string; border: string }
> = {
  Persona: {
    dot: "#FFB3E6",
    bg: "bg-[#FFB3E6]/10",
    text: "text-[#FFB3E6]",
    border: "border-l-[#FFB3E6]",
  },
  Outcome: {
    dot: "#A4DD00",
    bg: "bg-[#A4DD00]/10",
    text: "text-[#A4DD00]",
    border: "border-l-[#A4DD00]",
  },
  Scenario: {
    dot: "#569480",
    bg: "bg-[#569480]/15",
    text: "text-[#7FB8A2]",
    border: "border-l-[#569480]",
  },
  Step: {
    dot: "#AD7BE9",
    bg: "bg-[#AD7BE9]/10",
    text: "text-[#C29CEF]",
    border: "border-l-[#AD7BE9]",
  },
  Action: {
    dot: "#FB8500",
    bg: "bg-[#FB8500]/10",
    text: "text-[#FFA333]",
    border: "border-l-[#FB8500]",
  },
  Api: {
    dot: "#4C8EDA",
    bg: "bg-[#4C8EDA]/10",
    text: "text-[#78AEE3]",
    border: "border-l-[#4C8EDA]",
  },
};

export type FilterableLabel =
  | "Persona"
  | "Outcome"
  | "Scenario"
  | "Step"
  | "Action"
  | "Api";

export type FilterOperator =
  | "$eq"
  | "$containsi"
  | "$contains"
  | "$in"
  | "$startsWith"
  | "$startsWithi"
  | "$endsWith"
  | "$endsWithi"
  | "$regex"
  | "$regexi";

export interface FilterRow {
  id: string;
  label: FilterableLabel;
  attribute: string;
  operator: FilterOperator;
  value: string;
}

interface FunctionalGraphFilterPanelProps {
  applied: FilterRow[];
  onApply: (rows: FilterRow[]) => void;
  onClose: () => void;
  // BREEZEAI-1054: real custom-property keys discovered on the currently
  // fetched nodes, keyed by label. The panel surfaces these as autocomplete
  // suggestions on the custom-property input so users can pick the exact
  // case that's stored in Neo4j (a filter for `Mode` misses a persona whose
  // property is `mode` — Neo4j property names are case-sensitive).
  customPropertyKeys?: Partial<Record<FilterableLabel, string[]>>;
}

// Attributes users can filter on, per label. `numeric: true` triggers a
// number-type input so the value coerces cleanly on the backend. Fields mirror
// the graph-entity definitions in the backend (persona/outcome/scenario/step/
// action/api-graph.entity.ts).
//
// BREEZEAI-1054: array fields (e.g. `citationRefs`) are safe to filter on —
// `base-graph.service.ts` wraps the predicate in `ANY(val IN col WHERE …)` for
// every string operator via its `isArrayField` check. Labels here are
// annotated with the field type so users know they're matching against list
// elements (e.g. "Citation Ref (list)") or an internal embedding string.
const LABEL_ATTRIBUTES: Record<
  FilterableLabel,
  Array<{ value: string; label: string; numeric?: boolean }>
> = {
  Persona: [
    { value: "persona", label: "Name" },
    { value: "description", label: "Description" },
    { value: "nameKey", label: "Name Key" },
    { value: "embedText", label: "Embed Text" },
    { value: "citationRefs", label: "Citation Refs" },
  ],
  Outcome: [
    { value: "outcome", label: "Name" },
    { value: "description", label: "Description" },
    { value: "nameKey", label: "Name Key" },
    { value: "embedText", label: "Embed Text" },
    { value: "citationRefs", label: "Citation Refs" },
  ],
  Scenario: [
    { value: "scenario", label: "Name" },
    { value: "description", label: "Description" },
    { value: "nameKey", label: "Name Key" },
    { value: "embedText", label: "Embed Text" },
    { value: "citationRefs", label: "Citation Refs" },
  ],
  Step: [
    { value: "step", label: "Name" },
    { value: "description", label: "Description" },
    { value: "order", label: "Order", numeric: true },
    { value: "nameKey", label: "Name Key" },
    { value: "embedText", label: "Embed Text" },
    { value: "citationRefs", label: "Citation Refs" },
  ],
  Action: [
    { value: "action", label: "Name" },
    { value: "description", label: "Description" },
    { value: "order", label: "Order", numeric: true },
    { value: "nameKey", label: "Name Key" },
    { value: "embedText", label: "Embed Text" },
    { value: "citationRefs", label: "Citation Refs" },
  ],
  Api: [
    { value: "url", label: "URL" },
    { value: "method", label: "Method" },
    { value: "type", label: "Type" },
    { value: "description", label: "Description" },
    { value: "request", label: "Request" },
    { value: "response", label: "Response" },
  ],
};

// BREEZEAI-1054: sentinel that flips the attribute select into a free-text
// input for the property key. Users can add arbitrary properties via the Edit
// dialog's "Add Property" affordance, so the filter has to be able to target
// those too.
const CUSTOM_ATTR = "__custom__";

// Operators the backend supports on graph queries. Kept in sync with the switch
// in `FilterUtils.buildGraphFilters` and the Cypher builder in
// `base-graph.service.ts`. `$fulltext` is intentionally excluded — the
// underlying Neo4j fulltext index isn't project-scoped and leaks results
// across projects; add it back once the backend scopes it.
const OPERATOR_OPTIONS: Array<{
  value: FilterOperator;
  label: string;
  hint: string;
}> = [
  { value: "$containsi", label: "contains (any case)", hint: "text" },
  { value: "$contains", label: "contains", hint: "text" },
  { value: "$eq", label: "equals", hint: "exact" },
  { value: "$in", label: "in list", hint: "comma-separated" },
  { value: "$startsWithi", label: "starts with (any case)", hint: "prefix" },
  { value: "$startsWith", label: "starts with", hint: "prefix" },
  { value: "$endsWithi", label: "ends with (any case)", hint: "suffix" },
  { value: "$endsWith", label: "ends with", hint: "suffix" },
  { value: "$regexi", label: "regex (any case)", hint: "full-string, e.g. .*User.*" },
  { value: "$regex", label: "regex", hint: "full-string, e.g. .*User.*" },
];

const LABEL_OPTIONS: Array<{ value: FilterableLabel; label: string }> = [
  { value: "Persona", label: "Persona" },
  { value: "Outcome", label: "Task" },
  { value: "Scenario", label: "Scenario" },
  { value: "Step", label: "Step" },
  { value: "Action", label: "Action" },
  { value: "Api", label: "Api" },
];

let idCounter = 0;
const nextId = () => `f${Date.now()}_${++idCounter}`;

const makeEmptyRow = (label: FilterableLabel = "Persona"): FilterRow => ({
  id: nextId(),
  label,
  attribute: LABEL_ATTRIBUTES[label][0].value,
  operator: "$containsi",
  value: "",
});

// BREEZEAI-1054: themed autocomplete for the custom-property key input. The
// native `<datalist>` renders as an unstyled OS dropdown (black-on-white in
// dark mode, no border-radius, etc.) which looks out of place inside the
// panel. This component reads the same suggestion list and pops a themed
// menu below the input on focus, filtered as the user types. Free-form
// typing still works (no lock-in to the suggestion list).
interface CustomPropertyInputProps {
  value: string;
  onChange: (v: string) => void;
  suggestions: string[];
}

function CustomPropertyInput({
  value,
  onChange,
  suggestions,
}: CustomPropertyInputProps) {
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState<number>(-1);
  const containerRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    const q = value.trim().toLowerCase();
    // Substring match; when empty, show everything so users can discover keys.
    const list = suggestions.filter(
      (s) => q === "" || s.toLowerCase().includes(q),
    );
    // De-dupe: don't offer the exact value the user has already typed.
    return list.filter((s) => s !== value);
  }, [suggestions, value]);

  // Close on click outside.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const commit = (v: string) => {
    onChange(v);
    setOpen(false);
    setActiveIdx(-1);
  };

  return (
    <div ref={containerRef} className="relative">
      <Input
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          if (!open) setOpen(true);
          setActiveIdx(-1);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (!open || filtered.length === 0) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIdx((i) => Math.min(filtered.length - 1, i + 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIdx((i) => Math.max(0, i - 1));
          } else if (e.key === "Enter" && activeIdx >= 0) {
            e.preventDefault();
            commit(filtered[activeIdx]);
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
        placeholder="Property name"
        autoComplete="off"
        className="h-8 text-xs bg-surface-input border-surface-border text-white placeholder:text-muted/70"
      />
      {open && filtered.length > 0 && (
        <div
          role="listbox"
          className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto rounded-md border border-surface-border bg-surface-card shadow-xl backdrop-blur-md"
        >
          {filtered.map((s, i) => (
            <button
              key={s}
              type="button"
              role="option"
              aria-selected={i === activeIdx}
              onMouseDown={(e) => {
                // mousedown, not click: fires before the input's blur so the
                // container hasn't unmounted the list yet.
                e.preventDefault();
                commit(s);
              }}
              onMouseEnter={() => setActiveIdx(i)}
              className={`block w-full text-left px-2.5 py-1.5 text-xs transition-colors ${
                i === activeIdx
                  ? "bg-surface-hover text-white"
                  : "text-text-primary hover:bg-surface-hover"
              }`}
            >
              <span className="font-mono">{s}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function FunctionalGraphFilterPanel({
  applied,
  onApply,
  onClose,
  customPropertyKeys,
}: FunctionalGraphFilterPanelProps) {
  const [rows, setRows] = useState<FilterRow[]>(() => {
    if (applied.length === 0) return [makeEmptyRow()];
    // BREEZEAI-1054: keep custom-attribute rows editable across re-opens.
    // Standard fields stay in the select; anything else is treated as a
    // custom property so the user can adjust the key without retyping.
    return applied.map((r) => ({ ...r }));
  });

  // Track which rows are in "custom property" mode independently of the
  // attribute value — so the user can wipe the input to "" without the row
  // silently reverting to a canned attribute.
  const [customMode, setCustomMode] = useState<Record<string, boolean>>(() => {
    const out: Record<string, boolean> = {};
    for (const r of applied) {
      if (!LABEL_ATTRIBUTES[r.label].some((a) => a.value === r.attribute)) {
        out[r.id] = true;
      }
    }
    return out;
  });

  const updateRow = (id: string, patch: Partial<FilterRow>) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        const next = { ...r, ...patch };
        // Changing the label snaps back to a canned attribute — and exits
        // "custom property" mode for that row so the select re-shows.
        if (patch.label && patch.label !== r.label) {
          next.attribute = LABEL_ATTRIBUTES[patch.label][0].value;
          setCustomMode((m) => {
            if (!m[id]) return m;
            const { [id]: _dropped, ...rest } = m;
            return rest;
          });
        }
        return next;
      })
    );
  };

  const removeRow = (id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
    setCustomMode((m) => {
      if (!m[id]) return m;
      const { [id]: _dropped, ...rest } = m;
      return rest;
    });
  };

  const addRow = () => {
    setRows((prev) => [...prev, makeEmptyRow()]);
  };

  const handleApply = () => {
    // BREEZEAI-1054: trim both `attribute` and `value` — a stray space in a
    // custom property name (e.g. "Data ") becomes `filters[Data%20]` on the
    // wire and never matches any Neo4j property, so the filter would silently
    // drop.
    const cleaned = rows
      .map((r) => ({
        ...r,
        attribute: r.attribute.trim(),
        value: r.value.trim(),
      }))
      .filter((r) => r.attribute && r.value !== "");

    // BREEZEAI-1229: a comma in the value only makes sense for the "in list"
    // operator — every other operator treats "Admin,User" as a literal string
    // and returns nothing. Regex ops are exempt (commas can appear inside a
    // pattern). Check every row so the user sees the first bad one.
    for (const r of cleaned) {
      if (
        r.value.includes(",") &&
        r.operator !== "$in" &&
        r.operator !== "$regex" &&
        r.operator !== "$regexi"
      ) {
        toast.error(
          `To match multiple values, change the operator to "in list".`,
        );
        return;
      }
    }

    // BREEZEAI-1229: same-attribute-same-operator rows OR-compose on the
    // wire — see `filtersForLabel` in use-functional-graph-pagination.tsx
    // where duplicates are coalesced into a regex alternation (or merged
    // `$in` for equality/list). Two `Persona · Name · contains Admin` +
    // `contains Customer` rows return both personas, matching the
    // ticket-expected OR behaviour. No block here.

    onApply(cleaned);
  };

  const handleClearAll = () => {
    setRows([makeEmptyRow()]);
    onApply([]);
  };

  return (
    <div className="w-[620px] bg-surface border border-surface-border rounded-lg shadow-2xl overflow-hidden">
      {/* Header — icon + title on the left, dismiss on the right. Flat bg
          (no tint strip) to match the standard modal convention used by the
          Upload Documents dialog; separation comes from the border alone. */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-surface-border">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-lg bg-primary/15 flex items-center justify-center">
            <Filter className="h-3.5 w-3.5 text-primary" />
          </div>
          <div>
            <p className="text-sm font-semibold text-white leading-tight">
              Filter nodes
            </p>
            <p className="text-[11px] text-muted leading-tight">
              Match on any label attribute
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Close filter panel"
          className="h-7 w-7 text-muted hover:text-white hover:bg-surface-hover rounded-md"
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Body — each row is its own card with a colored left rail matching
          the label; delete lives inline top-right so a mostly-empty row
          isn't cluttered. */}
      <div className="p-3 space-y-2.5 max-h-[55vh] overflow-y-auto">
        {rows.length === 0 && (
          <div className="text-center py-6">
            <p className="text-xs text-muted">
              No filters yet. Click <span className="text-white font-medium">Add filter</span> to start.
            </p>
          </div>
        )}

        {rows.map((row) => {
          const attributes = LABEL_ATTRIBUTES[row.label];
          const attrDef = attributes.find((a) => a.value === row.attribute);
          const isNumeric = !!attrDef?.numeric;
          const accent = LABEL_ACCENT[row.label];
          return (
            <div
              key={row.id}
              className={`rounded-lg border border-surface-border bg-surface-dark border-l-[3px] ${accent.border} p-2 hover:bg-surface-hover transition-colors`}
            >
              {/* Single-row layout: label pill · attribute · operator · value · delete.
                  Widths are fixed for pill/attribute/operator so the value input
                  gets the flex — that's where the user is typing. */}
              <div className="grid grid-cols-[110px_120px_140px_1fr_28px] gap-2 items-center">
                {/* Label pill — colored badge with a select embedded so
                    changing the label is one click on the pill itself. */}
                <div
                  className={`flex items-center gap-1.5 px-2 h-8 rounded-md ${accent.bg}`}
                  title="Node label"
                >
                  <span
                    aria-hidden
                    className="flex-shrink-0 inline-block h-1.5 w-1.5 rounded-full"
                    style={{ backgroundColor: accent.dot }}
                  />
                  <Select
                    value={row.label}
                    onValueChange={(v) =>
                      updateRow(row.id, { label: v as FilterableLabel })
                    }
                  >
                    <SelectTrigger
                      className={`h-6 border-0 bg-transparent text-[11px] font-semibold uppercase tracking-wide px-0 gap-1 focus:ring-0 focus:ring-offset-0 min-w-0 ${accent.text}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-surface-card border-surface-border">
                      {LABEL_OPTIONS.map((opt) => (
                        <SelectItem
                          key={opt.value}
                          value={opt.value}
                          className="text-white hover:bg-surface-hover focus:bg-surface-hover text-xs"
                        >
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* BREEZEAI-1054: standard attributes come from the label's
                    canned list. Picking "Custom property…" flips this cell into
                    a free-text input so the user can filter on any property
                    they've added via the Edit dialog's Add Property. */}
                {customMode[row.id] ? (
                  <CustomPropertyInput
                    value={row.attribute}
                    onChange={(v) => updateRow(row.id, { attribute: v })}
                    suggestions={customPropertyKeys?.[row.label] ?? []}
                  />
                ) : (
                  <Select
                    value={row.attribute}
                    onValueChange={(v) => {
                      if (v === CUSTOM_ATTR) {
                        setCustomMode((m) => ({ ...m, [row.id]: true }));
                        updateRow(row.id, { attribute: "" });
                      } else {
                        updateRow(row.id, { attribute: v });
                      }
                    }}
                  >
                    <SelectTrigger className="h-8 text-xs bg-surface-input border-surface-border text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-surface-card border-surface-border">
                      {attributes.map((opt) => (
                        <SelectItem
                          key={opt.value}
                          value={opt.value}
                          className="text-white hover:bg-surface-hover focus:bg-surface-hover text-xs"
                        >
                          {opt.label}
                        </SelectItem>
                      ))}
                      <SelectItem
                        key={CUSTOM_ATTR}
                        value={CUSTOM_ATTR}
                        className="text-primary hover:bg-surface-hover focus:bg-surface-hover text-xs border-t border-surface-border/50 mt-1"
                      >
                        + Custom property…
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}

                <Select
                  value={row.operator}
                  onValueChange={(v) =>
                    updateRow(row.id, { operator: v as FilterOperator })
                  }
                >
                  <SelectTrigger className="h-8 text-xs bg-surface-input border-surface-border text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-surface-card border-surface-border">
                    {OPERATOR_OPTIONS.map((opt) => (
                      <SelectItem
                        key={opt.value}
                        value={opt.value}
                        className="text-white hover:bg-surface-hover focus:bg-surface-hover text-xs"
                      >
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Input
                  value={row.value}
                  onChange={(e) => updateRow(row.id, { value: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleApply();
                    }
                  }}
                  placeholder={
                    row.operator === "$in"
                      ? "a, b, c"
                      : row.operator === "$regex" || row.operator === "$regexi"
                      ? ".*value.*"
                      : isNumeric
                      ? "0"
                      : "Value…"
                  }
                  type={
                    isNumeric &&
                    row.operator !== "$in" &&
                    row.operator !== "$regex" &&
                    row.operator !== "$regexi"
                      ? "number"
                      : "text"
                  }
                  className="h-8 text-xs bg-surface-input border-surface-border text-white placeholder:text-muted/70"
                />

                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted hover:text-red-400 hover:bg-red-500/10"
                  onClick={() => removeRow(row.id)}
                  aria-label="Remove filter"
                  title="Remove filter"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          );
        })}

        {/* Dashed add-filter button — reads as an obvious call-to-action but
            doesn't compete with the primary Apply button below. */}
        <button
          type="button"
          onClick={addRow}
          className="w-full h-9 flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-surface-border text-xs text-muted hover:text-white hover:border-primary/60 hover:bg-primary/5 transition-colors"
        >
          <Plus className="h-3.5 w-3.5" />
          Add filter
        </button>
      </div>

      {/* Footer — clear on the left as a subtle secondary, Apply on the
          right as the solid primary action. */}
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-surface-border">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClearAll}
          className="h-8 text-xs text-muted hover:text-white hover:bg-surface-hover"
        >
          Clear all
        </Button>
        <Button
          size="sm"
          onClick={handleApply}
          className="h-8 px-4 text-xs bg-primary hover:bg-primary/90 shadow-sm"
          style={{ color: "#ffffff" }}
        >
          Apply filters
        </Button>
      </div>
    </div>
  );
}
